import React, { useEffect, useRef, useState } from "react";
import Logo from "../../assets/gallery/Logo.png";

/** Sections on the landing page (make sure each exists with the exact id) */
const MENU = [
  { label: "Home", href: "#home" },
  { label: "Leistungen", href: "#services" },
  { label: "Galerie", href: "#gallery" },
  { label: "Über uns", href: "#about" },
  { label: "Kontakt", href: "#contact" },
];

/** Easing + animated scroll (fallback when native smooth is unavailable) */
const easeInOutCubic = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

function jsAnimatedScrollTo(targetY, duration = 200) {
  const startY = window.scrollY || window.pageYOffset || 0;
  const delta = targetY - startY;
  let start;

  return new Promise((resolve) => {
    const step = (ts) => {
      if (start == null) start = ts;
      const elapsed = ts - start;
      const t = Math.min(1, elapsed / duration);
      window.scrollTo(0, Math.round(startY + delta * easeInOutCubic(t)));
      if (elapsed < duration) requestAnimationFrame(step);
      else resolve();
    };
    requestAnimationFrame(step);
  });
}

/** Normalize href -> target id (supports "#id" or "/#id") */
function hrefToId(href) {
  if (!href) return null;
  const m = href.match(/#(.+)$/);
  return m ? m[1] : null;
}

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");
  const headerRef = useRef(null);
  const btnRef = useRef(null);
  const drawerRef = useRef(null);

  const brandBg = "bg-[#0a6c7b]";
  const navShadow = "shadow-[0_1px_0_0_rgba(255,255,255,0.08)]";

  const getNavH = () => headerRef.current?.offsetHeight || 80;

  // Lock body when drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  // Close on outside click / Esc
  useEffect(() => {
    const onDocClick = (e) => {
      if (!open) return;
      const insideMenu = drawerRef.current && drawerRef.current.contains(e.target);
      const insideBtn = btnRef.current && btnRef.current.contains(e.target);
      if (!insideMenu && !insideBtn) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // Reserve space for fixed header so content isn't hidden underneath
  useEffect(() => {
    const applyPad = () => {
      const h = headerRef.current ? headerRef.current.offsetHeight : 0;
      document.body.style.paddingTop = h ? `${h}px` : "";
      document.documentElement.style.setProperty("--nav-h", `${h}px`);
    };
    applyPad();

    const ro = new ResizeObserver(applyPad);
    if (headerRef.current) ro.observe(headerRef.current);
    window.addEventListener("resize", applyPad);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", applyPad);
      document.body.style.paddingTop = "";
      document.documentElement.style.removeProperty("--nav-h");
    };
  }, []);

  // Active link highlight via IntersectionObserver
  useEffect(() => {
    const ids = MENU.map((m) => hrefToId(m.href)).filter(Boolean);
    const sections = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!sections.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        const topMost = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (topMost) setActive(topMost.target.id);
      },
      {
        rootMargin: `-${getNavH() + 8}px 0px -55% 0px`,
        threshold: [0.1, 0.25, 0.5, 0.75],
      }
    );
    sections.forEach((sec) => io.observe(sec));
    return () => io.disconnect();
  }, []);

  // Smooth-scroll on initial load if URL already has a hash
  useEffect(() => {
    if (!window.location.hash) return;
    const id = window.location.hash.replace("#", "");
    const el = document.getElementById(id);
    if (!el) return;

    requestAnimationFrame(() => {
      const supportsSmooth = "scrollBehavior" in document.documentElement.style;
      if (supportsSmooth) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        const y = el.getBoundingClientRect().top + window.scrollY - (getNavH() + 12);
        jsAnimatedScrollTo(y, 900);
      }
    });
  }, []);

  const smoothScrollToEl = async (el) => {
    if (!el) return false;
    const supportsSmooth = "scrollBehavior" in document.documentElement.style;

    if (supportsSmooth) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return true;
    } else {
      const y = el.getBoundingClientRect().top + window.scrollY - (getNavH() + 12);
      await jsAnimatedScrollTo(y, 900);
      return true;
    }
  };

  const scrollToId = async (id) => {
    const el = document.getElementById(id);
    if (!el) return false;
    await smoothScrollToEl(el);
    return true;
  };

  const onNavigate = async (e, href) => {
    const id = hrefToId(href);
    if (!id) {
      // External/route link -> let normal navigation happen
      return;
    }
    e.preventDefault();

    const onSamePage = !!document.getElementById(id);

    if (onSamePage) {
      const ok = await scrollToId(id);
      if (ok) {
        window.history.replaceState(null, "", `#${id}`);
        setOpen(false);
      }
    } else {
      window.location.assign(`/${href.startsWith("#") ? href : `#${id}`}`);
    }
  };

  return (
    <header
      ref={headerRef}
      className={`fixed top-0 left-0 right-0 z-50 ${brandBg} ${navShadow} text-white`}
      role="banner"
    >
      {/* Inner */}
      <div className="mx-auto flex h-20 max-w-7xl items-center gap-4 px-4 sm:px-6 lg:px-8">
        {/* Brand */}
        <a href="/" className="flex items-center gap-3 shrink-0" aria-label="Go to homepage">
          <img
            src={Logo}
            alt="Company logo"
            className="pl-5 h-[100px] w-[150px] sm:h-[100px] sm:w-[150px] object-contain select-none pointer-events-none"
          />
        </a>

        {/* Desktop menu */}
        <nav className="ml-auto hidden md:block" aria-label="Primary">
          <ul className="flex items-center gap-6">
            {MENU.map((item) => {
              const id = hrefToId(item.href);
              const isActive = active && id && id === active;
              return (
                <li key={item.label}>
                  <a
                    href={item.href}
                    onClick={(e) => onNavigate(e, item.href)}
                    className={[
                      "relative text-xs font-extrabold uppercase tracking-widest transition-opacity",
                      isActive ? "opacity-100" : "opacity-90 hover:opacity-100",
                    ].join(" ")}
                  >
                    {item.label}
                    <span
                      className={[
                        "pointer-events-none absolute left-0 -bottom-2 h-[2px] rounded bg-white",
                        "transition-all duration-200",
                        isActive ? "w-full" : "w-0",
                      ].join(" ")}
                    />
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Mobile toggle */}
        <button
          ref={btnRef}
          aria-label="Toggle navigation"
          aria-expanded={open}
          aria-controls="mobile-menu"
          onClick={() => setOpen((v) => !v)}
          className="ml-auto inline-flex items-center justify-center rounded-md p-2 md:hidden hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          <span className="relative block h-5 w-6">
            <span
              className={`absolute left-0 top-0 h-0.5 w-6 bg-white transition ${
                open ? "translate-y-2 rotate-45" : ""
              }`}
            />
            <span
              className={`absolute left-0 top-2 h-0.5 w-6 bg-white transition ${
                open ? "opacity-0" : "opacity-100"
              }`}
            />
            <span
              className={`absolute left-0 top-4 h-0.5 w-6 bg-white transition ${
                open ? "-translate-y-2 -rotate-45" : ""
              }`}
            />
          </span>
        </button>
      </div>

      {/* Mobile overlay */}
      <div
        onClick={() => setOpen(false)}
        aria-hidden="true"
        className={[
          "fixed inset-0 z-40 bg-black/40 transition-opacity md:hidden",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        ].join(" ")}
      />

      {/* Mobile drawer */}
      <nav
        ref={drawerRef}
        id="mobile-menu"
        aria-label="Primary mobile"
        className={[
          "fixed inset-y-0 right-0 z-50 w-[86vw] max-w-sm bg-[#0a6c7b] text-white md:hidden",
          "transition-transform duration-300",
          open ? "translate-x-0" : "translate-x-full",
          "flex flex-col p-4",
        ].join(" ")}
      >
        <div className="mb-2 mt-1 flex items-center gap-3">
          <img src={Logo} alt="" className="h-80px w-20 object-contain" />
        </div>
        <ul className="mt-2 flex flex-col gap-1">
          {MENU.map((item) => (
            <li key={item.label}>
              <a
                href={item.href}
                onClick={(e) => onNavigate(e, item.href)}
                className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-3 text-xs font-extrabold uppercase tracking-widest hover:bg-white/10"
              >
                {item.label}
                <svg
                  className="opacity-90"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  aria-hidden="true"
                >
                  <path
                    d="M9 18l6-6-6-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            </li>
          ))}
          <li className="mt-2">
            <a
              href="#contact"
              onClick={(e) => onNavigate(e, "#contact")}
              className="inline-flex w-full items-center justify-center rounded-full border-2 border-white/90 px-4 py-2 text-sm font-black tracking-wide hover:border-white transition"
            >
              Kostenloses Angebot
            </a>
          </li>
        </ul>
        <div className="mt-auto pt-4 text-[11px] opacity-80">
          © {new Date().getFullYear()} GVSI Clean Care
        </div>
      </nav>
    </header>
  );
}
